import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";

export class StringMagnetic extends StringModule {
  constructor(context: StringContext) {
    super(context);
    this.htmlKey = "magnetic";

    this.attributesToMap = [
      ...this.attributesToMap,
      { key: "strength", type: "number", fallback: this.settings["strength"] },
      { key: "radius", type: "number", fallback: this.settings["radius"] },
    ];
  }

  override initializeObject(
    globalId: number,
    object: StringObject,
    element: HTMLElement,
    attributes: Record<string, any>
  ): void {
    super.initializeObject(globalId, object, element, attributes);
    object.setProperty("is-magneting", false);
    object.setProperty("magnetic-target-x", 0);
    object.setProperty("magnetic-target-y", 0);
    object.setProperty("magnetic-x", 0);
    object.setProperty("magnetic-y", 0);
    object.setProperty("lerp", 0.1);
  }

  onMouseMove(e: MouseEvent): void {
    this.objects.forEach((object) => {
      const element = object.htmlElement as HTMLElement;
      const rect = element.getBoundingClientRect();
      const centerX =
        rect.left + (object.getProperty<number>("half-width") ?? 0);
      const centerY =
        rect.top + (object.getProperty<number>("half-height") ?? 0);
      const dx = e.clientX - centerX;
      const dy = e.clientY - centerY;
      const distance = Math.sqrt(dx ** 2 + dy ** 2);

      const radius = object.getProperty<number>("radius") ?? 0;
      const strength = object.getProperty<number>("strength") ?? 0;

      const factor = this.tools.magneticPull.process({
        distance,
        radius,
        strength,
      });

      object.setProperty("magnetic-target-x", dx * factor);
      object.setProperty("magnetic-target-y", dy * factor);
      if (factor > 0) {
        object.setProperty("is-magneting", true);
      }
    });
  }

  onFrame(data: StringData): void {
    this.objects.forEach((object) => {
      if (object.getProperty("is-magneting")) {
        let magneticX = object.getProperty<number>("magnetic-x") ?? 0;
        let magneticY = object.getProperty<number>("magnetic-y") ?? 0;

        let lerp = object.getProperty<number>("lerp") ?? 0;

        let targetMagneticX =
          object.getProperty<number>("magnetic-target-x") ?? 0;
        let targetMagneticY =
          object.getProperty<number>("magnetic-target-y") ?? 0;

        let lerpX = this.tools.lerp.process({
          from: magneticX,
          to: targetMagneticX,
          progress: lerp,
        });
        let lerpY = this.tools.lerp.process({
          from: magneticY,
          to: targetMagneticY,
          progress: lerp,
        });

        if (lerpX > -0.01 && lerpX < 0.01) {
          lerpX = 0;
          object.setProperty(
            "magnetic-x",
            object.getProperty("magnetic-target-x")
          );
        }
        if (lerpY > -0.01 && lerpY < 0.01) {
          lerpY = 0;
          object.setProperty(
            "magnetic-y",
            object.getProperty("magnetic-target-y")
          );
        }
        magneticX += lerpX;
        magneticY += lerpY;
        object.setProperty<number>("magnetic-x", magneticX);
        object.setProperty<number>("magnetic-y", magneticY);
        this.events.emit(`magnetic:move:${object.id}`, {
          x: magneticX,
          y: magneticY,
        });

        this.applyToElementAndConnects(object, (el) => {
          el.style.setProperty("--magnetic-x", magneticX.toString());
          el.style.setProperty("--magnetic-y", magneticY.toString());
        });

        if (
          object.getProperty("magnetic-target-x") == magneticX ||
          object.getProperty("magnetic-target-y") == magneticY
        ) {
          object.setProperty("is-magneting", false);
        }
      }
    });
  }
}
